import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CACHE_KEY_TICKETS } from "../../../utils/constants";
import { TicketInterface } from "../../../models/TicketModule/Ticket";
import ticketService from "../../../services/ticketService/ticketService";

interface UpdateTicketsContext {
  previousTickets: TicketInterface[];
}

const useUpdateTicketStatus = (page: number, pageSize: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    TicketInterface,
    Error,
    TicketInterface,
    UpdateTicketsContext
  >({
    mutationFn: async (ticket: TicketInterface) => {
      const { id, statusId } = ticket;
      return ticketService.UpdateStatus(id || "", statusId);
    },

    onMutate: async (updatedTicket: TicketInterface) => {
      await queryClient.cancelQueries({
        queryKey: [CACHE_KEY_TICKETS, page, pageSize],
      });

      const previousTickets =
        queryClient.getQueryData<TicketInterface[]>([
          CACHE_KEY_TICKETS,
          page,
          pageSize,
        ]) || [];

      queryClient.setQueryData<TicketInterface[]>(
        [CACHE_KEY_TICKETS, page, pageSize],
        (tickets = []) =>
          tickets.map((ticket) =>
            ticket.id === updatedTicket.id ? updatedTicket : ticket
          )
      );

      return { previousTickets };
    },

    onSuccess: (savedTicket) => {
      queryClient.setQueryData<TicketInterface[]>(
        [CACHE_KEY_TICKETS, page, pageSize],
        (tickets) =>
          tickets?.map((ticket) =>
            ticket.id === savedTicket.id ? savedTicket : ticket
          )
      );
    },

    onError: (error, updatedTicket, context) => {
      if (!context) return;

      queryClient.setQueryData<TicketInterface[]>(
        [CACHE_KEY_TICKETS, page, pageSize],
        context.previousTickets
      );
    },
  });
};

export default useUpdateTicketStatus;
